import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Spin, Table, Typography, Space, Tag } from "antd";
import {
  DownloadOutlined,
  FileOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import {
  parseExcelBlobForPreview,
  type ExcelPreviewData,
} from "../../utils/excelPreviewUtils";
import mammoth from "mammoth";

const { Text } = Typography;

type PreviewFileType = "pdf" | "image" | "excel" | "word" | "other";

const getPreviewFileType = (fileName: string): PreviewFileType => {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext))
    return "image";
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (["doc", "docx"].includes(ext)) return "word";
  return "other";
};

const getFileIcon = (type: PreviewFileType) => {
  switch (type) {
    case "pdf":
      return <FilePdfOutlined />;
    case "excel":
      return <FileExcelOutlined />;
    case "word":
      return <FileWordOutlined />;
    default:
      return <FileOutlined />;
  }
};

interface DocumentPreviewModalProps {
  visible: boolean;
  onClose: () => void;
  fileName: string;
  blobUrl: string | null;
  onDownload?: () => void;
}

const ExcelPreview: React.FC<{ data: ExcelPreviewData }> = ({ data }) => {
  if (data.rows.length === 0) {
    return <Text type="secondary">The spreadsheet is empty.</Text>;
  }

  const [headerRow, ...bodyRows] = data.rows;

  const columns = (headerRow || []).map((header, idx) => ({
    title: header || `Col ${idx + 1}`,
    dataIndex: String(idx),
    key: String(idx),
    ellipsis: true,
    width: 150,
  }));

  const dataSource = bodyRows.map((row, rowIdx) => {
    const record: Record<string, string> = { key: String(rowIdx) };
    row.forEach((cell, colIdx) => {
      record[String(colIdx)] = cell;
    });
    return record;
  });

  return (
    <div>
      <div
        style={{
          marginBottom: 8,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text type="secondary">Sheet: {data.sheetName}</Text>
        {data.truncated && (
          <Tag color="warning">
            Showing first 50 rows — download for full file
          </Tag>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: "max-content", y: 500 }}
        size="small"
        bordered
      />
    </div>
  );
};

const WordPreview: React.FC<{ html: string }> = ({ html }) => {
  return (
    <div
      style={{
        padding: 24,
        background: "#fff",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        maxHeight: "65vh",
        overflow: "auto",
        lineHeight: 1.6,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  visible,
  onClose,
  fileName,
  blobUrl,
  onDownload,
}) => {
  const [loading, setLoading] = useState(false);
  const [excelData, setExcelData] = useState<ExcelPreviewData | null>(null);
  const [wordHtml, setWordHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastParsedUrl = useRef<string | null>(null);

  const fileType = getPreviewFileType(fileName);

  useEffect(() => {
    if (!visible || !blobUrl) {
      return;
    }

    if (lastParsedUrl.current === blobUrl) return;

    const needsParsing = fileType === "excel" || fileType === "word";
    if (!needsParsing) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setExcelData(null);
    setWordHtml(null);

    (async () => {
      try {
        const response = await fetch(blobUrl);
        const blob = await response.blob();

        if (cancelled) return;

        if (fileType === "excel") {
          const data = await parseExcelBlobForPreview(blob);
          if (!cancelled) {
            setExcelData(data);
            lastParsedUrl.current = blobUrl;
          }
        } else if (fileType === "word") {
          const ext = fileName.split(".").pop()?.toLowerCase();
          if (ext === "doc") {
            if (!cancelled) {
              setError(
                "Preview is not available for .doc files. Please download the file instead."
              );
            }
          } else {
            const arrayBuffer = await blob.arrayBuffer();
            const result = await mammoth.convertToHtml({
              arrayBuffer,
            });
            if (!cancelled) {
              setWordHtml(result.value);
              lastParsedUrl.current = blobUrl;
            }
          }
        }
      } catch (err) {
        console.error("Error parsing document for preview:", err);
        if (!cancelled) {
          setError("Failed to load preview. Try downloading the file instead.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, blobUrl, fileType, fileName]);

  const handleClose = () => {
    setExcelData(null);
    setWordHtml(null);
    setError(null);
    lastParsedUrl.current = null;
    onClose();
  };

  const renderContent = () => {
    if (!blobUrl) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      );
    }

    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading preview...</Text>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <FileOutlined style={{ fontSize: 48, color: "#999" }} />
          <p style={{ marginTop: 16 }}>{error}</p>
          {onDownload && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={onDownload}
            >
              Download File
            </Button>
          )}
        </div>
      );
    }

    switch (fileType) {
      case "pdf":
        return (
          <iframe
            src={blobUrl}
            style={{
              width: "100%",
              height: "70vh",
              border: "none",
              minHeight: 500,
            }}
            title={`Preview: ${fileName}`}
          />
        );
      case "image":
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <img
              src={blobUrl}
              alt={fileName}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          </div>
        );
      case "excel":
        return excelData ? (
          <ExcelPreview data={excelData} />
        ) : null;
      case "word":
        return wordHtml ? (
          <WordPreview html={wordHtml} />
        ) : null;
      default:
        return (
          <div style={{ textAlign: "center", padding: 40 }}>
            <FileOutlined style={{ fontSize: 48, color: "#999" }} />
            <p style={{ marginTop: 16 }}>
              Preview is not available for this file type.
            </p>
            {onDownload && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={onDownload}
              >
                Download File
              </Button>
            )}
          </div>
        );
    }
  };

  return (
    <Modal
      title={
        <Space>
          {getFileIcon(fileType)}
          <span>{fileName || "Document Preview"}</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={
        <Space>
          {onDownload && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={onDownload}
            >
              Download
            </Button>
          )}
          <Button onClick={handleClose}>Close</Button>
        </Space>
      }
      width={fileType === "excel" ? 1000 : 900}
      style={{ top: 20 }}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

export default DocumentPreviewModal;
